/**
 * Secret picker — a `<select>` populated from `api.secrets.listForOrg(orgId)`.
 * "(none)" is a valid option so an access method can deliberately have no
 * credential. Shows the secret's fingerprint underneath the select so the
 * operator can sanity-check which key they picked without leaving the dialog.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Loader2, ExternalLink } from 'lucide-react';
import { api, type Secret } from '@/api/client.js';

interface Props {
  orgId: string;
  value: string;
  onChange: (id: string) => void;
  /** When provided, this id is shown selected even if it didn't make the list yet. */
  fallbackLabel?: string;
}

export function SecretPicker({ orgId, value, onChange }: Props): JSX.Element {
  const query = useQuery({
    queryKey: ['secrets', 'for-org', orgId],
    queryFn: () => api.secrets.listForOrg(orgId),
    enabled: !!orgId,
  });

  const selected: Secret | undefined = (query.data ?? []).find((s) => s.id === value);
  const fingerprint = selected?.metadata?.['fingerprint'] as string | undefined;
  const publicKey = selected?.metadata?.['publicKey'] as string | undefined;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={query.isLoading || !orgId}
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
        >
          <option value="">— none —</option>
          {(query.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
              {s.metadata?.['kind'] ? ` (${s.metadata['kind']})` : ''}
            </option>
          ))}
        </select>
        {query.isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
      </div>
      {selected ? (
        <div className="space-y-0.5 text-[11px] text-muted-foreground">
          {fingerprint && <p className="break-all font-mono">{fingerprint}</p>}
          {publicKey && (
            <p className="break-all font-mono">{publicKey.slice(0, 80)}{publicKey.length > 80 ? '…' : ''}</p>
          )}
          <Link
            to="/p/applications/secrets/$id"
            params={{ id: selected.id }}
            search={{ org: selected.orgSlug }}
            className="inline-flex items-center gap-1 hover:text-foreground"
          >
            <ExternalLink className="h-2.5 w-2.5" />
            Open secret
          </Link>
        </div>
      ) : (
        <p className="text-[11px] text-muted-foreground">
          No secret attached. Create one on the Secrets page first, then come back to pick it.
        </p>
      )}
    </div>
  );
}
